import { useState } from "react";
import { toast } from "sonner";
import { BillStatus } from "@boo/shared";
import { Button } from "@/components/ui/button";
import { useBillStore } from "../store/useBillStore";
import { formatBaht } from "../lib/billMath";
import type { BillEditor } from "../types/bills.types";
import PersonAvatar from "./PersonAvatar";

const ALL = "__all__";

/** Owner override: settle a friend's share with no slip (e.g. paid in person). */
export default function UnpaidFriends({ editor }: { editor: BillEditor }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  if (editor.status !== BillStatus.Finalized) return null;
  const unpaid = editor.people.filter((p) => !p.isOwner && !editor.paid[p.id]);
  if (unpaid.length === 0) return null;

  async function resolve(id: string, name: string) {
    setBusyId(id);
    try {
      await useBillStore.getState().markPersonPaid(id);
      toast.success(`${name} marked paid`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusyId(null);
    }
  }

  async function resolveAll() {
    setBusyId(ALL);
    try {
      for (const p of unpaid) {
        await useBillStore.getState().markPersonPaid(p.id);
      }
      toast.success(`${unpaid.length} marked paid`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className="space-y-2 rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-foreground">Not paid yet</h2>
        {unpaid.length > 1 && (
          <Button
            size="sm"
            variant="outline"
            className="shrink-0"
            disabled={busyId !== null}
            onClick={resolveAll}
          >
            Resolve all
          </Button>
        )}
      </div>
      <ul className="space-y-2">
        {unpaid.map((p) => (
          <li key={p.id} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 flex-1 items-center gap-2 truncate text-sm text-foreground">
              <PersonAvatar person={p} size={24} />
              {p.name}{" "}
              <span className="text-muted-foreground [font-variant-numeric:tabular-nums]">
                {formatBaht(editor.finalAmounts[p.id] ?? 0)}
              </span>
            </span>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={busyId !== null}
              onClick={() => resolve(p.id, p.name)}
            >
              Resolve anyway
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
